import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Printer, Save } from 'lucide-react';
import { fabSheetTotals, printFabTmSheet } from '../../lib/fabTmPrint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shop`;
const money = (v) => `$${(Math.round(v * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

// Fabrication T&M sheets for the week — one per WO with shop hours. Markups prefill from the fab defaults and can be
// changed for this print; the Fab Manager / office can save them as the new standard.
export function FabTmReport({ data, canSaveMarkups }) {
  const [markups, setMarkups] = useState({ consumables: data.markups.consumables, materials: data.markups.materials });
  useEffect(() => { setMarkups({ consumables: data.markups.consumables, materials: data.markups.materials }); }, [data]);

  const saveDefaults = async () => {
    try {
      await axios.put(`${API}/fab-markups`, markups);
      toast.success('Saved as the standard fab markups');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  const print = (wo) => { if (!printFabTmSheet(data, markups, wo)) toast.error('Pop-up blocked — allow pop-ups to print'); };

  return (
    <div className="bg-white rounded-xl border border-slate-300" data-testid="fab-tm-report">
      <div className="px-4 py-3 bg-slate-900/10 border-b border-slate-300 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[220px]">
          <p className="text-lg text-slate-900 font-bold">Fabrication T&amp;M Sheets</p>
          <p className="text-xs text-slate-700">Week {data.date_from} to {data.date_to} · shop labour at FAB ${data.labour_rates.FAB.regular}/h reg · ${data.labour_rates.FAB.overtime}/h OT</p>
        </div>
        <label className="text-xs text-slate-800 font-semibold">Consumables markup %
          <input type="number" value={markups.consumables} onChange={(e) => setMarkups({ ...markups, consumables: e.target.value })} onFocus={(e) => e.target.select()}
            className="mt-1 block w-24 px-2 py-1.5 bg-white border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="fabtm-markup-consumables" />
        </label>
        <label className="text-xs text-slate-800 font-semibold">Materials markup %
          <input type="number" value={markups.materials} onChange={(e) => setMarkups({ ...markups, materials: e.target.value })} onFocus={(e) => e.target.select()}
            className="mt-1 block w-24 px-2 py-1.5 bg-white border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="fabtm-markup-materials" />
        </label>
        {canSaveMarkups && (
          <button onClick={saveDefaults} className="px-3 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-900 text-xs font-semibold inline-flex items-center gap-1" data-testid="fabtm-save-markups">
            <Save className="w-3.5 h-3.5" /> Save as standard
          </button>
        )}
        {data.work_orders.length > 0 && (
          <button onClick={() => print(null)} className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-700 text-white text-xs font-semibold inline-flex items-center gap-1" data-testid="fabtm-print-all">
            <Printer className="w-3.5 h-3.5" /> Print all ({data.work_orders.length})
          </button>
        )}
      </div>
      {data.work_orders.length === 0 ? (
        <div className="text-center py-12 text-slate-700">No shop hours on any WO this week</div>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-100 text-slate-900">
              <th className="px-3 py-2 text-left">WO#</th>
              <th className="px-2 py-2 text-left">Customer / job</th>
              <th className="px-2 py-2 text-left">Crew</th>
              <th className="px-2 py-2 text-right">Reg h</th>
              <th className="px-2 py-2 text-right">OT h</th>
              <th className="px-2 py-2 text-right">Labour</th>
              <th className="px-2 py-2 text-right">Consumables</th>
              <th className="px-2 py-2 text-right">Materials</th>
              <th className="px-2 py-2 text-right">Total</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.work_orders.map((wo) => {
              const t = fabSheetTotals(wo, data, markups);
              return (
                <tr key={wo.work_order_number} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`fabtm-row-${wo.work_order_number}`}>
                  <td className="px-3 py-2 font-mono font-bold text-blue-700">{wo.work_order_number}</td>
                  <td className="px-2 py-2 text-slate-900"><span className="font-semibold">{wo.customer_name}</span>{wo.description ? <span className="text-slate-600"> — {wo.description}</span> : null}
                    {wo.pending_hours > 0 && <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1">{wo.pending_hours}h not approved</span>}</td>
                  <td className="px-2 py-2 text-slate-700">{wo.employees.map((e) => e.employee_name.split(' ')[0]).join(', ')}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{t.reg || ''}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-amber-700">{t.ot || ''}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{money(t.labour)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{t.consumables ? money(t.consumables) : '—'}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{t.materials ? money(t.materials) : '—'}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-bold text-slate-900">{money(t.total)}</td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => print(wo.work_order_number)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-800 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded px-2 py-1" data-testid={`fabtm-print-${wo.work_order_number}`}>
                      <Printer className="w-3 h-3" /> Print
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default FabTmReport;
